import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import AddEditBannerDialog from "../../../components/Dialog/AddEditBannerDialog";
import GlobalTable from "../../../components/Tables/GlobalTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import { getBanner, updateBanner } from "../../../services/banner";
import { handleOpenSnackBar } from "../../../utils";
import { BANNER_POSITION } from "../../../constants/Variables";
import { useTranslation } from "react-i18next";

const headerData = [
    "createdAt",
    "order",
    "title",
    "position",
    "link",
    "status",
    "actions",
];

const tableRows = [
    "created_date",
    "order",
    "banner_title",
    "banner_position",
    "banner_link",
    "status",
    "actions",
];

function Banner() {
    const dispatch = useDispatch();
    const openDialog = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [allBanners, setAllBanners] = useState([]);
    const { t } = useTranslation(["catalogue", "common"]);
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    const debouncedSearchTerm = useDebounce(searchKeys.bannerName, 1000);

    const fetchAllBanners = async (name) => {
        try {
            setIsLoading(true);
            const response = await getBanner(name);
            if (response.data.status === "success") {
                let data = response?.data.data.reverse();
                setAllBanners(data);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllBanners(debouncedSearchTerm);
    }, [debouncedSearchTerm]);

    const handleEdit = (params) => {
        openDialog.current.openDialog({
            type: "edit",
            data: params.row,
            getData: fetchAllBanners,
        });
    };

    const handleChangeStatus = async (event, row, key) => {
        try {
            let payload = {
                [key]: event,
            };
            setIsLoading(true);
            const response = await updateBanner(row._id, payload);
            if (response.data.status === "success") {
                fetchAllBanners(searchKeys.bannerName);
                handleOpenSnackBar("success", response.data.message);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };


    const prepareExportData = () => {
        return allBanners.map((item) => ({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
            "Title In Romanian": item.title?.ro || "-",
            "Title In English": item.title?.en || "-",
            "Image Url Desktop In English": item.image?.desktop?.en || "-",
            "Image Url Desktop In Romanian": item.image?.desktop?.ro || "-",
            "Image Url Mobile In English": item.image?.mobile?.en || "-",
            "Image Url Mobile In Romanian": item.image?.mobile?.ro || "-",
            "Slug In English": item.mlSlug?.en || "-",
            "Slug In Romanian": item.mlSlug?.ro || "-",
            "Redirect Url": item.link || "-",
            "Position": BANNER_POSITION.find(item => item.value === item.position)?.label.en || "-",
            "Order": item.order || "-",
            "Status": item.status ? "Active" : "Inactive",
        }));
    };

    const renderHeaderAction = () => {
        return (
            <Stack
                gap={1}
                flexGrow={1}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-end"}
            >
                <GlobalButton
                    variant={"contained"}
                    btnText={"addNew"}
                    handleClickBtn={() => {
                        openDialog.current.openDialog({
                            type: "add",
                            data: null,
                            getData: fetchAllBanners,
                        });
                    }}
                    styleProps={{
                        width: "auto",
                        height: 40,
                    }}
                />
                <CSVLink
                    data={prepareExportData()}
                    filename={"banners.csv"}
                    className="csv-link"
                    target="_blank"
                >
                    <GlobalButton
                        variant={"export_button"}
                        btnText={"export"}
                        startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
                        styleProps={{
                            width: "auto",
                            height: 40,
                        }}
                    />
                </CSVLink>
            </Stack>
        );
    };

    const renderSearchAction = () => {
        return (
            <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
                <TextInput
                    styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
                    InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
                    InputEndIcon={
                        searchKeys.bannerName && (
                            <CancelOutlinedIcon
                                fontSize="small"
                                sx={{ cursor: "pointer" }}
                                onClick={() => {
                                    dispatch(
                                        setSearchKeys({
                                            ...searchKeys,
                                            bannerName: "",
                                        })
                                    );
                                }}
                            />
                        )
                    }
                    placeholder={t("searchByName", { ns: "common" })}
                    value={searchKeys.bannerName}
                    onChange={(e) => {
                        dispatch(
                            setSearchKeys({
                                ...searchKeys,
                                bannerName: e.target.value,
                            })
                        );
                    }}
                />
            </Stack>
        );
    };

    const renderTable = useMemo(() => {
        return (
            <GlobalTable
                minWidth={1200}
                tableHeader={headerData}
                displayRows={tableRows}
                tableData={allBanners || []}
                showActions={true}
                showEdit={true}
                handleEdit={handleEdit}
                handleChangeStatus={(e, row, key) => handleChangeStatus(e, row, "status")}
                handleChangeFeatured={(e, row, key) => handleChangeStatus(e, row, "isFeatured")}
            />
        );
    }, [allBanners]);

    return (
        <React.Fragment>
            <AppLoader isVisible={isLoading} />
            <PaperContainer
                title={t("bannerList")}
                subtitle={t("bannerDescription")}
                headerActions={renderHeaderAction()}
                searchActions={renderSearchAction()}
            >
                {renderTable}


                {allBanners?.length === 0 && isLoading === false && (
                    <Typography variant="h5" fontWeight={600} textAlign={"center"}>
                        {t("noDataFound", { ns: "common" })}
                    </Typography>
                )}
            </PaperContainer>

            <AddEditBannerDialog ref={openDialog} />

        </React.Fragment>
    );
}

export default Banner;
